// src/pages/team/CoinRelances.jsx
// Coin « Relances clients » — commercial (leads CRM à relancer) + administratif
// (factures impayées). Données lisibles par admin/team (RLS crm_leads + invoices).
// Accès : route staff + coin 'relances' (l'admin a tous les coins).
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { hasCoin, COIN_BY_KEY } from "../../config/teamCoins";

const COIN = COIN_BY_KEY.relances;
const DAY = 86400000;

const STAGE_LABEL = {
  lead: "Lead capté", qualifie: "Qualifié", demo: "Démo programmée",
  essai: "Essai en cours", client: "Client payant", perdu: "Perdu",
};
const NEXT_ACTION_LABEL = {
  appeler: "Appeler", message_linkedin: "Message LinkedIn", email: "Email",
  proposer_demo: "Proposer une démo", confirmer_demo: "Confirmer une démo",
  relance_webinaire: "Relancer (webinaire)", accompagner_essai: "Accompagner l'essai",
  verifier_activation: "Vérifier l'activation", relance_avant_fin_essai: "Relancer avant fin d'essai",
  cloturer_perdu: "Clôturer (perdu)",
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—");
const euros = (cents) => (cents == null ? "—" : `${(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`);

function commercialAlerts(l) {
  const now = Date.now();
  const stageAt = new Date(l.stage_updated_at || l.created_at).getTime();
  const created = new Date(l.created_at).getTime();
  const lastContact = l.last_contact_at ? new Date(l.last_contact_at).getTime() : null;
  const nextAt = l.next_action_at ? new Date(l.next_action_at).getTime() : null;
  const closed = l.stage === "client" || l.stage === "perdu";
  const out = [];
  if (l.stage === "lead" && !lastContact && now - created > DAY) out.push("Nouveau lead non contacté");
  if (l.stage === "qualifie" && now - stageAt > 3 * DAY) out.push("Qualifié sans démo");
  if (l.stage === "demo" && !nextAt && now - stageAt > 2 * DAY) out.push("Démo sans suite");
  if (nextAt && nextAt < now && !closed) out.push("Relance en retard");
  if (l.stage === "essai" && now - stageAt > 7 * DAY) out.push("Essai à risque");
  if (!closed && now - Math.max(stageAt, lastContact || 0) > 14 * DAY) out.push("Prospect bloqué");
  return out;
}

export default function CoinRelances() {
  const { profile, profileLoading } = useAuth();
  const [leads, setLeads] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const allowed = hasCoin(profile, "relances");

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const { data: cl } = await supabase
          .from("crm_leads")
          .select("id, full_name, structure, stage, stage_updated_at, created_at, last_contact_at, next_action_at, next_action_type, lead_score");
        setLeads(cl || []);
      } catch { setLeads([]); }
      try {
        const { data: inv } = await supabase
          .from("invoices")
          .select("id, buyer_name, amount_total, currency, status, hosted_invoice_url, created_at, stripe_customer_id")
          .in("status", ["open", "past_due", "uncollectible"])
          .order("created_at", { ascending: false })
          .limit(200);
        setInvoices(inv || []);
      } catch { setInvoices([]); }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const todo = useMemo(() => {
    return leads
      .map((l) => ({ lead: l, alerts: commercialAlerts(l) }))
      .filter((x) => x.alerts.length > 0)
      .sort((a, b) => (b.lead.lead_score || 0) - (a.lead.lead_score || 0));
  }, [leads]);

  if (profileLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f6f6f4]"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-gray-900" /></div>;
  }
  if (!allowed) {
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <div className="text-4xl" aria-hidden>🔒</div>
        <h1 className="mt-4 font-serif text-2xl font-semibold text-gray-900">Accès non accordé</h1>
        <p className="mt-2 text-sm text-gray-600">Le coin « Relances clients » ne vous a pas été attribué. Demandez-le à un administrateur.</p>
        <Link to="/team/dashboard" className="mt-6 inline-block rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800">Retour à mon espace</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f6f4]">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="text-3xl" aria-hidden>{COIN.emoji}</span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-600">Coin Relances</p>
              <h1 className="font-serif text-2xl font-semibold text-gray-950">{COIN.title}</h1>
            </div>
          </div>
          <Link to="/team/dashboard" className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">← Mon espace</Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-7">
        {/* KPI */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Kpi label="À relancer (commercial)" value={loading ? "…" : todo.length} tone="rose" />
          <Kpi label="Factures impayées" value={loading ? "…" : invoices.length} tone="amber" />
          <Kpi label="Montant en attente" value={loading ? "…" : euros(invoices.reduce((s, i) => s + (i.amount_total || 0), 0))} />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Commercial */}
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-950">Commercial — prospects à relancer</h2>
              <Link to="/admin/crm" className="text-sm font-medium text-violet-700 hover:underline">Ouvrir le CRM →</Link>
            </div>
            {loading ? <Muted>Chargement…</Muted> : todo.length === 0 ? (
              <Muted>Aucune relance commerciale en attente. 🎉</Muted>
            ) : (
              <div className="space-y-2">
                {todo.slice(0, 30).map(({ lead, alerts }) => (
                  <div key={lead.id} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900">{lead.full_name}</span>
                      <span className="text-[11px] text-gray-500">{STAGE_LABEL[lead.stage] || lead.stage}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {alerts.map((a) => <span key={a} className="rounded bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800 ring-1 ring-amber-200">{a}</span>)}
                    </div>
                    <div className="mt-1 text-[11px] text-gray-400">
                      {lead.structure || "—"} · prochaine action : {lead.next_action_type ? `${NEXT_ACTION_LABEL[lead.next_action_type] || lead.next_action_type} (${fmtDate(lead.next_action_at)})` : "à définir"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Administratif */}
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-950">Administratif — factures impayées</h2>
              <span className="text-xs text-gray-400">open · past_due · uncollectible</span>
            </div>
            {loading ? <Muted>Chargement…</Muted> : invoices.length === 0 ? (
              <Muted>Aucune facture impayée. 🎉</Muted>
            ) : (
              <div className="space-y-2">
                {invoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900">{inv.buyer_name || "Client"}</div>
                      <div className="text-[11px] text-gray-400">{fmtDate(inv.created_at)} · {inv.status}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="font-semibold text-gray-900">{euros(inv.amount_total)}</span>
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          Facture ↗
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function Kpi({ label, value, tone = "slate" }) {
  const tones = { slate: "text-gray-900", rose: "text-rose-700", amber: "text-amber-700" };
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}
function Muted({ children }) { return <div className="py-6 text-sm text-gray-400">{children}</div>; }
